import {
  buildCallerIdentityFromTokenClaims,
  canPerformCapability,
  mapContractRoleToPersona,
  normalizeRoleName,
  type CallerIdentity,
  type RoleName,
  type WorkspacePersonaRole,
} from '@myplayer/rbac';
import type { User } from 'firebase/auth';
import {
  ASSIGNABLE_MEMBER_ROLES,
  isSelfMember,
  mapSnapshotToMemberships,
  resolveDisplayedMemberRole,
} from './rbac-ui.js';

export {
  ASSIGNABLE_MEMBER_ROLES,
  isSelfMember,
  mapSnapshotToMemberships,
  resolveDisplayedMemberRole,
};

const ACTIVE_WORKSPACE_STORAGE_PREFIX = 'mp_admin_active_workspace';
const WORKSPACE_SCOPE_STORAGE_PREFIX = 'mp_admin_workspace_scope';

export const ALL_WORKSPACES_SCOPE_VALUE = 'ALL' as const;
export type WorkspaceScopeMode = 'ALL' | 'WORKSPACE';

export interface WorkspaceOption {
  readonly workspaceId: string;
  readonly name: string;
  readonly status?: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  readonly role: RoleName;
  readonly personaRole: WorkspacePersonaRole;
}

export interface AdminUserSession {
  readonly uid: string;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly emailVerified?: boolean;
  readonly orgId: string;
  readonly activeWorkspaceId: string;
  readonly role: RoleName;
  readonly personaRole: WorkspacePersonaRole;
  readonly callerIdentity: CallerIdentity;
}

export function getWorkspaceStorageKey(uid: string, orgId: string): string {
  return `${ACTIVE_WORKSPACE_STORAGE_PREFIX}:${orgId}:${uid}`;
}
export function getWorkspaceScopeStorageKey(uid: string, orgId: string): string {
  return `${WORKSPACE_SCOPE_STORAGE_PREFIX}:${orgId}:${uid}`;
}

export function canUseAllWorkspacesScope(
  session: AdminUserSession | null,
  availableWorkspaces: readonly WorkspaceOption[] = [],
): boolean {
  if (!session) return false;
  return (
    availableWorkspaces.length > 1 ||
    session.personaRole === 'ORG_ADMIN' ||
    session.personaRole === 'SUPER_ADMIN' ||
    canPerformCapability(session.callerIdentity, 'workspace:create', session.orgId)
  );
}

/**
 * Extracts `AdminUserSession` and `CallerIdentity` from Firebase Auth custom claims
 * using `@myplayer/rbac` role normalization and compact-claim mappings.
 */
export function buildSessionFromClaims(
  user: Pick<User, 'uid' | 'email' | 'displayName'> & { readonly emailVerified?: boolean },
  rawClaims: Readonly<Record<string, unknown>>,
): AdminUserSession {
  const c = buildCallerIdentityFromTokenClaims(user.uid, rawClaims);
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    ...(user.emailVerified !== undefined ? { emailVerified: user.emailVerified } : {}),
    orgId: c.orgId,
    activeWorkspaceId: c.activeWorkspaceId,
    role: c.effectiveRole,
    personaRole: c.personaRole,
    callerIdentity: c.callerIdentity,
  };
}

/**
 * Formats a workspace name so legacy placeholder values like "Default Workspace"
 * or "MyPlayer Default Workspace" never appear in the UI.
 */
export function formatWorkspaceName(rawName: string | undefined, workspaceId: string): string {
  const trimmed = rawName?.trim() ?? '';
  if (trimmed.length === 0 || /^(myplayer\s+)?default workspace$/i.test(trimmed)) {
    return workspaceId === 'ws_default' ? 'UK Retail' : workspaceId;
  }
  return trimmed;
}

/**
 * Computes the list of workspaces accessible to the current session, merging
 * custom-claim workspace roles with any workspace metadata loaded from Firestore.
 */
export function computeAvailableWorkspaces(
  session: AdminUserSession | null,
  firestoreWorkspaces: ReadonlyMap<
    string,
    { readonly name: string; readonly status?: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED' }
  > = new Map(),
): readonly WorkspaceOption[] {
  if (!session) return [];
  const isGlobalOrOrgAdmin = canPerformCapability(
    session.callerIdentity,
    'workspace:create',
    session.orgId,
  );
  const topAdminRole: RoleName = normalizeRoleName(session.callerIdentity.role);
  const resultMap = new Map<string, WorkspaceOption>();
  const addWs = (wsId: string, effectiveRole: RoleName): void => {
    const meta = firestoreWorkspaces.get(wsId);
    resultMap.set(wsId, {
      workspaceId: wsId,
      name: formatWorkspaceName(meta?.name, wsId),
      ...(meta?.status !== undefined ? { status: meta.status } : {}),
      role: effectiveRole,
      personaRole: mapContractRoleToPersona(effectiveRole),
    });
  };

  for (const [wsId, rawRole] of Object.entries(session.callerIdentity.workspaces ?? {})) {
    addWs(wsId, isGlobalOrOrgAdmin ? topAdminRole : normalizeRoleName(rawRole));
  }
  if (isGlobalOrOrgAdmin) {
    for (const wsId of firestoreWorkspaces.keys()) {
      if (!resultMap.has(wsId)) addWs(wsId, topAdminRole);
    }
  }
  if (!resultMap.has(session.activeWorkspaceId)) {
    addWs(session.activeWorkspaceId, session.role);
  }
  return Array.from(resultMap.values()).sort((a, b) => a.workspaceId.localeCompare(b.workspaceId));
}

/**
 * Returns an updated `AdminUserSession` switched to `targetWorkspaceId`,
 * or throws if the caller does not have permission to access that workspace.
 */
export function resolveSwitchedSession(
  current: AdminUserSession,
  targetWorkspaceId: string,
  availableWorkspaces?: readonly WorkspaceOption[],
): AdminUserSession {
  const trimmedTarget = targetWorkspaceId.trim();
  if (trimmedTarget.length === 0) throw new Error('Workspace ID cannot be empty.');
  const isGlobalOrOrgAdmin = canPerformCapability(
    current.callerIdentity,
    'workspace:create',
    current.orgId,
  );
  if (isGlobalOrOrgAdmin) {
    if (
      availableWorkspaces?.length &&
      !availableWorkspaces.some((w) => w.workspaceId === trimmedTarget)
    ) {
      throw new Error(
        `Workspace "${trimmedTarget}" does not belong to organisation "${current.orgId}".`,
      );
    }
    const topAdminRole: RoleName = normalizeRoleName(current.callerIdentity.role);
    return {
      ...current,
      activeWorkspaceId: trimmedTarget,
      role: topAdminRole,
      personaRole: mapContractRoleToPersona(topAdminRole),
    };
  }
  const claimRole = current.callerIdentity.workspaces?.[trimmedTarget];
  if (claimRole === undefined) {
    throw new Error(`You do not have access to workspace "${trimmedTarget}" in your role claims.`);
  }
  const normalizedRole = normalizeRoleName(claimRole);
  const personaRole = mapContractRoleToPersona(normalizedRole);
  return {
    ...current,
    activeWorkspaceId: trimmedTarget,
    role: normalizedRole,
    personaRole,
    callerIdentity: { ...current.callerIdentity, role: normalizedRole, personaRole },
  };
}

/**
 * Returns an updated `AdminUserSession` with the active persona role switched
 * in-memory for Admin Console role previewing / persona switching.
 */
export function resolveSwitchedPersonaSession(
  current: AdminUserSession,
  targetPersonaRole: WorkspacePersonaRole,
): AdminUserSession {
  const normalizedRole = normalizeRoleName(targetPersonaRole);
  const wsId = current.activeWorkspaceId;
  return {
    ...current,
    role: normalizedRole,
    personaRole: targetPersonaRole,
    callerIdentity: {
      ...current.callerIdentity,
      role: normalizedRole,
      personaRole: targetPersonaRole,
      workspaces: { ...(current.callerIdentity.workspaces ?? {}), [wsId]: normalizedRole },
      workspacePersonas: {
        ...(current.callerIdentity.workspacePersonas ?? {}),
        [wsId]: targetPersonaRole,
      },
    },
  };
}

export const MYPLAYER_APPS = [
  'resolver|GS1 Resolver|/overview',
  'projects|Projects|https://projects.myplayr.co.uk',
  'create|Create|https://create.myplayr.co.uk',
  'account|Account|https://account.myplayr.co.uk',
] as const;

export function getRoleShortLabel(role: WorkspacePersonaRole): string {
  return role === 'SUPER_ADMIN'
    ? 'Super'
    : role === 'ORG_ADMIN'
      ? 'Owner'
      : role === 'WORKSPACE_MANAGER'
        ? 'Admin'
        : 'Role';
}

export function registerOutsideMenusDismiss(
  active: boolean,
  refs: readonly { readonly current: HTMLElement | null }[],
  onDismiss: () => void,
): (() => void) | undefined {
  if (!active || typeof document === 'undefined') return undefined;
  const onMouse = (e: MouseEvent): void => {
    if (e.target instanceof Node && refs.every((r) => !r.current?.contains(e.target as Node))) {
      onDismiss();
    }
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') onDismiss();
  };
  document.addEventListener('mousedown', onMouse);
  document.addEventListener('keydown', onKey);
  return () => {
    document.removeEventListener('mousedown', onMouse);
    document.removeEventListener('keydown', onKey);
  };
}

export function buildWorkspaceMenuItems(
  session: AdminUserSession,
  availableWorkspaces: readonly WorkspaceOption[],
  workspaceScope: WorkspaceScopeMode,
  canViewAllWorkspaces: boolean,
) {
  const allItem = canViewAllWorkspaces
    ? [
        {
          id: ALL_WORKSPACES_SCOPE_VALUE,
          name: 'All workspaces (Organisation)',
          initials: 'All',
          isCurrent: workspaceScope === 'ALL',
          testId: 'profile-switch-all-workspaces' as string | undefined,
        },
      ]
    : [];
  return [
    ...allItem,
    ...availableWorkspaces.map((ws) => ({
      id: ws.workspaceId,
      name: ws.name,
      initials: ws.name.replace(/^ws_/i, '').slice(0, 2).toUpperCase(),
      isCurrent: workspaceScope !== 'ALL' && ws.workspaceId === session.activeWorkspaceId,
      testId: `profile-switch-workspace-${ws.workspaceId}` as string | undefined,
    })),
  ];
}

export function computeNextAdminTheme(current: 'system' | 'light' | 'dark'): 'light' | 'dark' {
  const sysDark =
    current === 'system' &&
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return current === 'dark' || sysDark ? 'light' : 'dark';
}
